import {
  computed,
  shallowRef,
  type ComputedRef,
  type WritableComputedRef,
} from "vue";

type Entry<D> = D & { open: boolean };

/**
 * One open dialog out of a family, each entry carrying only the ids its dialog reads the store by.
 *
 * Closing a dialog only drops `open`: the entry stays until another dialog opens, so a closing modal
 * keeps its content through the fade-out.
 */
export function useActiveDialog<D extends { kind: string }>() {
  const active = shallowRef<Entry<D> | null>(null);

  const isOpen = (kind: D["kind"]) =>
    active.value?.open === true && active.value.kind === kind;

  const open = (dialog: D) => {
    active.value = { ...dialog, open: true };
  };

  /** Closes the dialog only if it is still the open one: an action may land after the user moved on. */
  const close = (kind: D["kind"]) => {
    if (isOpen(kind) && active.value !== null)
      active.value = { ...active.value, open: false };
  };

  /**
   * Patches `dialog` with what its action answered, unless it was closed or replaced meanwhile: the
   * answer belongs to the dialog that sent it.
   */
  const update = (dialog: Entry<D>, patch: Partial<D>) => {
    if (active.value === dialog) active.value = { ...dialog, ...patch };
  };

  /** The entry while it is of `kind`, open or fading out. */
  const entry = <K extends D["kind"]>(
    kind: K
  ): ComputedRef<Entry<Extract<D, { kind: K }>> | null> =>
    computed(() =>
      active.value?.kind === kind
        ? (active.value as Entry<Extract<D, { kind: K }>>)
        : null
    );

  /** A modal's `v-model`; the modals only ever write `false`, when dismissed. */
  const model = (kind: D["kind"]): WritableComputedRef<boolean> =>
    computed({
      get: () => isOpen(kind),
      set: (value: boolean) => {
        if (!value) close(kind);
      },
    });

  return { active, isOpen, open, close, update, entry, model };
}
